import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Loader } from '../components/ui/Loader';
import { ProxyCard } from '../components/proxies/ProxyCard';
import { useGetProxiesQuery } from '../store/api/proxiesApi';

type StatusFilter = 'active' | 'expired' | 'cancelled' | 'all';
type ServerFilter = 1 | 2 | 3 | 'all';

export function ProxiesListPage() {
  const { t } = useTranslation();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [serverFilter, setServerFilter] = useState<ServerFilter>('all');
  const [page, setPage] = useState(1);
  const limit = 20;

  const { data, isLoading, error } = useGetProxiesQuery({
    status: statusFilter !== 'all' ? statusFilter : undefined,
    serverId: serverFilter !== 'all' ? serverFilter : undefined,
    page,
    limit,
  });

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen relative">
        <div className="container mx-auto px-4 py-16 content-overlay">
          <div className="max-w-6xl mx-auto">
            <div className="flex justify-center items-center py-12">
              <Loader />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen relative">
        <div className="container mx-auto px-4 py-16 content-overlay">
          <div className="max-w-6xl mx-auto">
            <div className="card-glass rounded-2xl shadow-2xl p-8 text-center">
              <h2 className="text-2xl font-heading font-bold text-danger mb-4">
                {t('errors.network')}
              </h2>
              <p className="text-neutral-medium font-body mb-6">
                {'data' in (error as { data?: { message?: string } })
                  ? ((error as { data?: { message?: string } })?.data?.message || t('errors.network'))
                  : t('errors.network')}
              </p>
              <Link
                to="/proxies"
                className="inline-block px-6 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300"
              >
                {t('common.back')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const proxies = data?.data.proxies || [];
  const pagination = data?.data.pagination;

  return (
    <div className="min-h-screen relative">
      <div className="container mx-auto px-4 py-16 content-overlay">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8 text-center animate-slide-up">
            <h1 className="text-4xl md:text-5xl font-heading font-bold text-brand-accent mb-4">
              {t('proxies.list.title')}
            </h1>
          </div>

          {/* Filters */}
          <div className="card-glass rounded-2xl shadow-2xl p-6 mb-6 animate-scale-in">
            <div className="space-y-4">
              {/* Status Filter */}
              <div>
                <span className="text-neutral-medium font-body mb-2 block">
                  {t('proxies.list.filterStatus')}:
                </span>
                <div className="flex flex-wrap gap-2">
                  {(['all', 'active', 'expired', 'cancelled'] as StatusFilter[]).map((status) => (
                    <button
                      key={status}
                      onClick={() => {
                        setStatusFilter(status);
                        setPage(1);
                      }}
                      className={`px-4 py-2 rounded-lg font-heading font-semibold transition-all duration-300 ${
                        statusFilter === status
                          ? 'bg-brand-accent text-brand-primary'
                          : 'bg-neutral-dark text-neutral-light hover:bg-neutral-dark/80'
                      }`}
                    >
                      {status === 'all'
                        ? t('common.all')
                        : t(`proxies.status.${status}`)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Server Filter */}
              <div>
                <span className="text-neutral-medium font-body mb-2 block">
                  {t('proxies.list.filterServer')}:
                </span>
                <div className="flex flex-wrap gap-2">
                  {(['all', 1, 2, 3] as ServerFilter[]).map((server) => (
                    <button
                      key={server}
                      onClick={() => {
                        setServerFilter(server);
                        setPage(1);
                      }}
                      className={`px-4 py-2 rounded-lg font-heading font-semibold transition-all duration-300 ${
                        serverFilter === server
                          ? 'bg-brand-accent text-brand-primary'
                          : 'bg-neutral-dark text-neutral-light hover:bg-neutral-dark/80'
                      }`}
                    >
                      {server === 'all'
                        ? t('common.all')
                        : t(`proxies.server.server${server}`)}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Proxies List */}
          <div className="mb-6 animate-scale-in md:card-glass md:rounded-2xl md:shadow-2xl p-0 md:p-8">
            {proxies.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-neutral-medium text-lg font-body mb-4">
                  {t('proxies.list.empty')}
                </p>
                <Link
                  to="/proxies/purchase"
                  className="inline-block px-6 py-3 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300"
                >
                  {t('proxies.purchase.title')}
                </Link>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                {proxies.map((proxy) => (
                  <ProxyCard key={proxy._id} proxy={proxy} />
                ))}
              </div>
            )}
          </div>

          {/* Pagination */}
          {pagination && pagination.pages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <button
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
                className="px-4 py-2 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('common.previous')}
              </button>
              <span className="text-neutral-medium font-body px-4">
                {t('common.page')} {pagination.page} {t('common.of')} {pagination.pages}
              </span>
              <button
                onClick={() => handlePageChange(page + 1)}
                disabled={page === pagination.pages}
                className="px-4 py-2 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('common.next')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

